import { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders, handleAuthError, getAdminToken } from '../utils/authUtils';
import { getApiBaseUrlWithoutPath } from '../../config/environment';
import { productService } from '../../services/api';
import { readProductStatusFlags } from '../utils/productStatusFlags';
import { getAdminUser } from '../utils/authUtils';
import {
  canSetProductFeatured,
  omitProductFeaturedForNonAdmin,
} from '../constants/teamAccessPermissions';
import { fetchAllAdminProducts } from '../utils/fetchAdminProductTotal';
import { extractAdminListTotal } from '../utils/adminListTotal';

const useProducts = (filters = {}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0
  });

  const fetchProducts = useCallback(async (newFilters = {}) => {
    try {
      setLoading(true);
      setError(null);

      const headers = getAuthHeaders();

      console.log('🛍️ Fetching all admin products (paged catalog)...');

      const { products: allRows, pagination: pg } = await fetchAllAdminProducts(headers);

      if (allRows) {
        console.log('✅ Products loaded successfully:', allRows.length, 'products');
        setProducts(allRows.map((p) => ({ ...p, ...readProductStatusFlags(p) })));
        const listEnvelope = { success: true, data: allRows, pagination: pg };
        const totalFromApi = extractAdminListTotal(listEnvelope);
        const resolvedTotal =
          totalFromApi != null && totalFromApi >= allRows.length
            ? totalFromApi
            : allRows.length;
        setPagination({
          ...(pg || {}),
          page: 1,
          limit: allRows.length,
          total: resolvedTotal,
          totalPages: 1,
        });
      } else {
        throw new Error('Failed to fetch products');
      }
    } catch (error) {
      console.error('❌ Products error:', error);
      setError(error.message);

      // Fallback: single-page fetch (legacy path)
      try {
        const headers = getAuthHeaders();
        const apiUrl = getApiBaseUrlWithoutPath();
        const queryParams = new URLSearchParams({
          page: pagination.page,
          limit: pagination.limit,
          ...newFilters,
        });
        const response = await fetch(`${apiUrl}/api/admin/products?${queryParams}`, {
          method: 'GET',
          headers,
        });
        if (!response.ok) {
          handleAuthError(new Error(`HTTP ${response.status}`), response);
        }
        const result = await response.json();
        if (result.success) {
          const rows = Array.isArray(result.data) ? result.data : [];
          setProducts(rows.map((p) => ({ ...p, ...readProductStatusFlags(p) })));
          setPagination(result.pagination);
          return;
        }
      } catch {
        /* use outer error state */
      }

      setProducts([]);
      setPagination({
        page: 1,
        limit: 100,
        total: 0,
        totalPages: 0,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const addProduct = async (productData) => {
    try {
      setLoading(true);
      setError(null);

      const token = getAdminToken();
      if (!token) {
        throw new Error('No admin token found');
      }

      const apiUrl = getApiBaseUrlWithoutPath();
      
      console.log('➕ Adding new product:', productData);

      const adminUser = getAdminUser();
      productData = omitProductFeaturedForNonAdmin(adminUser, productData);

      // Check if product has images - use FormData if yes, JSON if no
      const hasImages = productData.images && productData.images.length > 0;
      
      let response;
      
      if (hasImages) {
        // Use FormData for products with images
        const formData = new FormData();
        
        // Add product data
        formData.append('name', productData.name);
        formData.append('description', productData.description);
        formData.append('category', productData.category);
        formData.append('brand', productData.brand);
        formData.append('price', (productData.originalPrice || productData.price).toString());
        formData.append('subcategory', productData.subcategory || '');
        formData.append('specifications.gender', productData.gender || 'Unisex');
        
        // Add optional fields
        if (canSetProductFeatured(adminUser) && productData.isFeatured !== undefined) {
          formData.append('isFeatured', productData.isFeatured.toString());
        }
        if (productData.isNew !== undefined) {
          formData.append('isNew', productData.isNew.toString());
        }
        if (productData.isOnSale !== undefined) {
          formData.append('isOnSale', productData.isOnSale.toString());
        }
        
        // Add images
        productData.images.forEach((file) => {
          formData.append('images', file);
        });

        console.log('📤 Using FormData for product with images');
        
        response = await fetch(`${apiUrl}/api/admin/products/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
      } else {
        // Use JSON for products without images
        console.log('📤 Using JSON for product without images');
        
        response = await fetch(`${apiUrl}/api/admin/products`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: productData.name,
            description: productData.description,
            category: productData.category,
            brand: productData.brand,
            price: parseFloat(productData.originalPrice || productData.price),
            subcategory: productData.subcategory,
            specifications: {
              gender: productData.gender || 'Unisex'
            },
            ...(canSetProductFeatured(adminUser)
              ? { isFeatured: productData.isFeatured || false }
              : {}),
            isNew: productData.isNew || false,
            isOnSale: productData.isOnSale || false,
            colors: productData.colors || [],
            sizes: productData.sizes || []
          })
        });
      }

      console.log('➕ Add product response status:', response.status);

      if (!response.ok) {
        const errorResult = await response.json();
        console.log('❌ Add product failed:', errorResult);
        handleAuthError(new Error(`HTTP ${response.status}: ${errorResult.message || 'Unknown error'}`), response);
      }

      const result = await response.json();
      console.log('✅ Product added successfully:', result);
      
      if (result.success) {
        await fetchProducts(); // Refresh the products list
        return result;
      } else {
        throw new Error(result.message || 'Failed to add product');
      }
    } catch (error) {
      console.error('❌ Add product error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateProduct = async (productId, productData) => {
    try {
      setLoading(true);
      setError(null);

      const token = getAdminToken();
      if (!token) {
        throw new Error('No admin token found');
      }

      const apiUrl = getApiBaseUrlWithoutPath();
      
      console.log('🔄 Updating product:', productId, productData);
      
      // Try the standard update endpoint first
      try {
        const response = await fetch(`${apiUrl}/api/admin/products/${productId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(productData)
        });

        console.log('🔄 Update response status:', response.status);

        if (response.ok) {
          const result = await response.json();
          console.log('✅ Product updated successfully:', result);
          if (result.success) {
            await fetchProducts(); // Refresh the products list
            return result;
          }
        } else {
          const errorResult = await response.json();
          console.log('❌ Update failed:', errorResult);
          throw new Error(errorResult.message || `HTTP ${response.status}`);
        }
      } catch (updateError) {
        console.log('❌ Standard update failed:', updateError.message);
        throw updateError;
      }
    } catch (error) {
      console.error('❌ Update product error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteProduct = async (productId) => {
    try {
      setLoading(true);
      setError(null);

      const result = await productService.deleteProduct(productId);

      if (result?.success) {
        await fetchProducts();
        return result;
      }
      throw new Error(result?.message || result?.error || 'Failed to delete product');
    } catch (error) {
      console.error('Delete product error:', error);
      const msg = error?.message || 'Failed to delete product';
      setError(msg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const clearAllProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = getAuthHeaders();
      const apiUrl = getApiBaseUrlWithoutPath();

      const response = await fetch(`${apiUrl}/api/admin/products/clear-all`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        handleAuthError(new Error(`HTTP ${response.status}`), response);
      }

      const result = await response.json();
      
      if (result.success) {
        await fetchProducts(); // Refresh the products list
        return result;
      } else {
        throw new Error(result.message || 'Failed to clear all products');
      }
    } catch (error) {
      console.error('Clear all products error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const clearDemoProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = getAuthHeaders();
      const apiUrl = getApiBaseUrlWithoutPath();

      // Get all products first to identify demo products
      const response = await fetch(`${apiUrl}/api/admin/products?limit=1000`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        handleAuthError(new Error(`HTTP ${response.status}`), response);
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        // Filter demo products (you can customize this logic)
        const demoProducts = result.data.filter(product => 
          product.name?.toLowerCase().includes('demo') ||
          product.name?.toLowerCase().includes('test') ||
          product.description?.toLowerCase().includes('demo') ||
          product.description?.toLowerCase().includes('test') ||
          product.brand?.toLowerCase().includes('demo') ||
          product.brand?.toLowerCase().includes('test')
        );

        console.log(`Found ${demoProducts.length} demo products to delete`);

        // Delete demo products one by one
        for (const product of demoProducts) {
          try {
            const deleteResponse = await fetch(`${apiUrl}/api/admin/products/${product._id || product.id}`, {
              method: 'DELETE',
              headers
            });

            if (deleteResponse.ok) {
              console.log(`Deleted demo product: ${product.name}`);
            }
          } catch (deleteError) {
            console.error(`Error deleting product ${product.name}:`, deleteError);
          }
        }

        await fetchProducts(); // Refresh the products list
        return { success: true, deleted: demoProducts.length };
      } else {
        throw new Error(result.message || 'Failed to fetch products for demo cleanup');
      }
    } catch (error) {
      console.error('Clear demo products error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const uploadImage = async (file) => {
    try {
      const headers = getAuthHeaders();
      const apiUrl = getApiBaseUrlWithoutPath();

      console.log('📤 Uploading image to Cloudinary:', file.name);

      const formData = new FormData();
      formData.append('images', file); // Changed from 'image' to 'images'

      const response = await fetch(`${apiUrl}/api/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': headers.Authorization
        },
        body: formData
      });

      console.log('📤 Upload response status:', response.status);

      if (!response.ok) {
        const errorResult = await response.json();
        console.error('❌ Upload failed:', errorResult);
        handleAuthError(new Error(`HTTP ${response.status}: ${errorResult.message || 'Upload failed'}`), response);
      }

      const result = await response.json();
      console.log('📤 Upload response:', result);
      
      if (result.success && result.data && result.data.uploads && result.data.uploads.length > 0) {
        const uploadedImage = result.data.uploads[0];
        console.log('✅ Image uploaded successfully:', uploadedImage.url);
        return {
          success: true,
          url: uploadedImage.url,
          public_id: uploadedImage.public_id,
          asset_id: uploadedImage.asset_id,
          width: uploadedImage.width,
          height: uploadedImage.height,
          format: uploadedImage.format,
          bytes: uploadedImage.bytes,
          originalName: uploadedImage.originalName,
          uploadedAt: uploadedImage.uploadedAt
        };
      } else {
        throw new Error(result.message || 'No images uploaded');
      }
    } catch (error) {
      console.error('❌ Upload image error:', error);
      throw error;
    }
  };

  const uploadMultipleImages = async (files) => {
    try {
      const headers = getAuthHeaders();
      const apiUrl = getApiBaseUrlWithoutPath();

      console.log('📤 Uploading multiple images to Cloudinary:', files.length);

      const formData = new FormData();
      files.forEach(file => {
        formData.append('images', file);
      });

      const response = await fetch(`${apiUrl}/api/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': headers.Authorization
        },
        body: formData
      });

      console.log('📤 Multiple upload response status:', response.status);

      if (!response.ok) {
        const errorResult = await response.json();
        console.error('❌ Multiple upload failed:', errorResult);
        handleAuthError(new Error(`HTTP ${response.status}: ${errorResult.message || 'Upload failed'}`), response);
      }

      const result = await response.json();
      console.log('📤 Multiple upload response:', result);
      
      if (result.success && result.data && result.data.uploads && result.data.uploads.length > 0) {
        console.log('✅ Multiple images uploaded successfully:', result.data.uploads.length);
        return result.data.uploads.map(uploadedImage => ({
          success: true,
          url: uploadedImage.url,
          public_id: uploadedImage.public_id,
          asset_id: uploadedImage.asset_id,
          width: uploadedImage.width,
          height: uploadedImage.height,
          format: uploadedImage.format,
          bytes: uploadedImage.bytes,
          originalName: uploadedImage.originalName,
          uploadedAt: uploadedImage.uploadedAt
        }));
      } else {
        throw new Error(result.message || 'No images uploaded');
      }
    } catch (error) {
      console.error('❌ Upload multiple images error:', error);
      throw error;
    }
  };

  return { 
    products, 
    loading, 
    error, 
    pagination, 
    refetch: fetchProducts,
    addProduct,
    updateProduct,
    deleteProduct,
    clearAllProducts,
    clearDemoProducts,
    uploadImage,
    uploadMultipleImages
  };
};

export default useProducts;
